import { lazy, Suspense } from "react";
import Layout from "@/components/Layout";
import HeroSection from "@/components/HeroSection";
import { Link } from "react-router-dom";
import { useLazyVideo } from "@/hooks/useLazyVideo";

import {
  ArrowLeft,
  FolderSearch,
  Layers,
  Ruler,
} from "lucide-react";

const ProofAndComparison = lazy(() => import("@/components/ProofAndComparison"));
const TrustSection = lazy(() => import("@/components/TrustSection"));
const Faq = lazy(() => import("@/components/Faq"));
const ContactForm = lazy(() => import("@/components/ContactForm"));

const apps = [
  {
    brandName: "RevKit",
    subtitle: "ניהול רכיבים",
    nameColor: "text-green-500",
    description: "שליטה מלאה בספריות האובייקטים ושליפה מיידית של משפחות Revit ישירות לסביבת העבודה.",
    path: "/revkit",
    icon: <FolderSearch className="h-6 w-6" />,
    image: "/images/app-revkit.jpg",
    video: "/videos/home-revkit-component.mp4",
    buttonGradient: "bg-gradient-to-r from-green-400 to-emerald-600",
  },
  {
    brandName: "Listools",
    subtitle: "אוטומציית תיעוד",
    nameColor: "text-purple-500",
    description: "הפקה מהירה של דפי רשימות וחוברות ריכוז מדויקות לאלומיניום, נגרות ומסגרות.",
    path: "/listools",
    icon: <Layers className="h-6 w-6" />,
    image: "/images/app-listools.jpg",
    video: "/videos/home-listools-automation.mp4",
    buttonGradient: "bg-gradient-to-r from-purple-500 to-pink-500",
  },
  {
    brandName: "RapidRebar",
    subtitle: "דיטיילינג הנדסי",
    nameColor: "text-teal-500",
    description: "יצירת פרטי ברזל 2D ולוחות זמנים (Schedules) בתהליך עבודה זורם ותקני.",
    path: "/rapidrebar",
    icon: <Ruler className="h-6 w-6" />,
    image: "/images/app-rapidrebar.webp",
    video: "/videos/home-rapidrebar-detailing.mp4",
    bg: "bg-[hsl(220,13%,18%)]",
    buttonGradient: "bg-gradient-to-r from-teal-400 to-cyan-600",
  },
];

const faqItems = [
  {
    question: "באילו גרסאות Revit האפליקציות תומכות?",
    answer: "כל הכלים של Bimbee מותאמים לגרסאות 2021 עד 2025, עם התאמה טכנית שוטפת לעדכוני המערכת.",
  },
  {
    question: "כיצד מתבצעת הגישה לתקופת הניסיון?",
    answer: "ניתן להתקין ולהתחיל בשימוש מלא ל-14 ימים באופן מיידי. השירות פתוח לכולם ללא התחייבות כספית בשלב הניסיון.",
  },
  {
    question: "מהי המשמעות של מודל Local-first?",
    answer: "התוכנה פועלת ישירות על המעבד שלכם. התקשורת מול השרת מתבצעת אך ורק לצורך אימות תקינות המנוי.",
  },
  {
    question: "האם קיים מענה לשאלות טכניות?",
    answer: "צוות המומחים שלנו זמין למתן תמיכה והדרכה מקצועית במייל support@bimbee.com.",
  },
];

export default function Home() {
  const lazyVideoRef = useLazyVideo();

  return (
    <Layout>
      {/* 1. Hero */}
      <HeroSection />

      {/* 2. Hub Grid — "מוקדי היעילות של Bimbee" */}
      <section id="apps" className="relative py-16 md:py-24 bg-background" dir="rtl">
        <img
          src="/images/apps-section-bg.webp"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="container relative z-10 mb-10 md:mb-16">
          <h2 className="text-3xl font-bold text-foreground md:text-4xl">מוקדי היעילות של Bimbee</h2>
        </div>
        <div className="container relative z-10 grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-24">
          {apps.map((app) => (
            <Link
              key={app.brandName}
              to={app.path}
              
              className="group flex flex-col"
            >
              {/* Monitor mockup */}
              <div className="relative">
                {/* Screen bezel */}
                <div className="rounded-t-xl border border-[hsl(0,0%,82%)] bg-[hsl(0,0%,95%)] p-1.5 pb-0 shadow-lg">
                  {/* Screen */}
                  <div className={`relative overflow-hidden rounded-t-lg aspect-[16/10] ${app.bg || ''}`}>
                    {app.video ? (
                      <video
                        ref={lazyVideoRef}
                        data-src={app.video}
                        preload="none"
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <img
                        src={app.image}
                        alt={app.brandName}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    )}
                  </div>
                </div>
                {/* Stand base */}
                <div className="flex flex-col items-center">
                  <div className="h-1.5 w-full rounded-b-sm bg-[hsl(0,0%,88%)] border-x border-b border-[hsl(0,0%,82%)]" />
                  <div className="h-6 w-12 bg-[hsl(0,0%,85%)]" />
                  <div className="h-1.5 w-24 rounded-full bg-[hsl(0,0%,80%)]" />
                </div>
              </div>
              {/* Text below mockup */}
              <div className="mt-6 text-right flex-1 flex flex-col">
                <h3 className="text-xl font-bold mb-2">
                  <span className={app.nameColor}>{app.brandName}</span>
                  <span className="text-foreground"> | {app.subtitle}</span>
                </h3>
                <p className="text-muted-foreground text-base mb-4 leading-relaxed flex-1">{app.description}</p>
                <div className="mt-auto text-left">
                  <span className={`${app.buttonGradient} text-white text-sm font-bold tracking-[0.15em] uppercase rounded-full px-5 py-2 inline-flex items-center gap-2 shadow-md transition-opacity duration-300 group-hover:opacity-90`}>
                    למידע נוסף
                    <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-2" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 3. Proof + Comparison — combined dark container */}
      <Suspense fallback={null}>
        <ProofAndComparison />
      </Suspense>

      {/* 5. Why Bimbee */}
      <Suspense fallback={null}>
        <TrustSection />
      </Suspense>

      {/* 6. FAQ */}
      <Suspense fallback={null}>
        <Faq title="תשובות מקצועיות" items={faqItems} />
      </Suspense>

      {/* 7. Final CTA */}
      <section id="contact-form" className="gradient-cta py-16 md:py-20" dir="rtl">
        <div className="container text-center">
          <h2 className="text-3xl font-bold text-foreground md:text-4xl">הקפיצו את רמת הדיוק והתפוקה שלכם</h2>
          <div className="mt-10">
            <Suspense fallback={null}>
              <ContactForm />
            </Suspense>
          </div>
          <p className="mt-8 text-base text-foreground/70">
            השאירו פרטים ונחזור אליכם בהקדם
          </p>
        </div>
      </section>
    </Layout>
  );
}
